/**
 * Regiondefinisjoner og grid-generering for prescan.
 * Brukes av AdminPrescanDashboard for klient-side skanning.
 * Identisk logikk som scripts/prescan-utils.mjs generateGrid().
 */

export interface RegionBounds {
  name: string;
  country: 'Norge' | 'Sverige' | 'Finland' | 'Danmark';
  latMin: number;
  latMax: number;
  lonMin: number;
  lonMax: number;
}

export interface GridCell {
  latMin: number;
  latMax: number;
  lonMin: number;
  lonMax: number;
}

export const CELL_LAT = 0.15; // ~17 km nord–sør
export const CELL_LON = 0.28; // ~13 km øst–vest ved 65°N

export const NORWAY_REGIONS: RegionBounds[] = [
  { country: 'Norge', name: 'Østfold',          latMin: 59.0,  latMax: 59.65, lonMin: 10.5,  lonMax: 12.4  },
  { country: 'Norge', name: 'Akershus',         latMin: 59.6,  latMax: 60.35, lonMin: 10.0,  lonMax: 12.1  },
  { country: 'Norge', name: 'Oslo',             latMin: 59.78, latMax: 60.05, lonMin: 10.4,  lonMax: 10.98 },
  { country: 'Norge', name: 'Buskerud',         latMin: 59.3,  latMax: 61.2,  lonMin: 8.5,   lonMax: 10.9  },
  { country: 'Norge', name: 'Vestfold',         latMin: 59.0,  latMax: 59.75, lonMin: 9.85,  lonMax: 10.85 },
  { country: 'Norge', name: 'Innlandet',        latMin: 60.1,  latMax: 62.6,  lonMin: 8.5,   lonMax: 14.8  },
  { country: 'Norge', name: 'Telemark',         latMin: 58.9,  latMax: 60.3,  lonMin: 7.5,   lonMax: 10.0  },
  { country: 'Norge', name: 'Agder',            latMin: 57.85, latMax: 59.0,  lonMin: 6.2,   lonMax: 9.3   },
  { country: 'Norge', name: 'Rogaland',         latMin: 58.0,  latMax: 59.65, lonMin: 5.0,   lonMax: 7.6   },
  { country: 'Norge', name: 'Vestland',         latMin: 59.65, latMax: 62.5,  lonMin: 4.5,   lonMax: 7.6   },
  { country: 'Norge', name: 'Møre og Romsdal',  latMin: 62.0,  latMax: 63.65, lonMin: 5.0,   lonMax: 10.0  },
  { country: 'Norge', name: 'Trøndelag',        latMin: 62.2,  latMax: 65.2,  lonMin: 9.0,   lonMax: 16.2  },
  { country: 'Norge', name: 'Nordland',         latMin: 65.0,  latMax: 69.1,  lonMin: 12.0,  lonMax: 18.5  },
  { country: 'Norge', name: 'Troms',            latMin: 68.5,  latMax: 70.5,  lonMin: 15.5,  lonMax: 22.5  },
  { country: 'Norge', name: 'Finnmark',         latMin: 69.4,  latMax: 71.2,  lonMin: 22.0,  lonMax: 31.2  },
];

export const SWEDEN_REGIONS: RegionBounds[] = [
  // Södra Sverige
  { country: 'Sverige', name: 'Skåne',                   latMin: 55.3,  latMax: 56.5,  lonMin: 12.8,  lonMax: 14.5  },
  { country: 'Sverige', name: 'Blekinge',                latMin: 56.0,  latMax: 56.5,  lonMin: 14.4,  lonMax: 16.0  },
  { country: 'Sverige', name: 'Halland',                 latMin: 56.4,  latMax: 57.5,  lonMin: 12.0,  lonMax: 13.5  },
  { country: 'Sverige', name: 'Kronoberg',               latMin: 56.5,  latMax: 57.3,  lonMin: 13.5,  lonMax: 15.5  },
  { country: 'Sverige', name: 'Jönköping',               latMin: 57.0,  latMax: 58.0,  lonMin: 13.3,  lonMax: 15.2  },
  { country: 'Sverige', name: 'Kalmar',                  latMin: 56.2,  latMax: 58.0,  lonMin: 15.2,  lonMax: 16.8  },
  { country: 'Sverige', name: 'Gotland',                 latMin: 57.0,  latMax: 58.0,  lonMin: 18.0,  lonMax: 19.4  },
  // Västra Sverige
  { country: 'Sverige', name: 'Västra Götaland',         latMin: 57.5,  latMax: 59.2,  lonMin: 11.1,  lonMax: 14.2  },
  { country: 'Sverige', name: 'Värmland',                latMin: 59.0,  latMax: 60.8,  lonMin: 11.6,  lonMax: 14.2  },
  // Östra Sverige
  { country: 'Sverige', name: 'Östergötland',            latMin: 58.0,  latMax: 59.0,  lonMin: 14.8,  lonMax: 16.8  },
  { country: 'Sverige', name: 'Södermanland',            latMin: 58.7,  latMax: 59.5,  lonMin: 15.5,  lonMax: 17.5  },
  { country: 'Sverige', name: 'Stockholm',               latMin: 59.0,  latMax: 60.0,  lonMin: 17.5,  lonMax: 19.2  },
  // Svealand
  { country: 'Sverige', name: 'Uppsala',                 latMin: 59.5,  latMax: 60.7,  lonMin: 16.5,  lonMax: 18.5  },
  { country: 'Sverige', name: 'Västmanland',             latMin: 59.3,  latMax: 60.2,  lonMin: 15.5,  lonMax: 16.8  },
  { country: 'Sverige', name: 'Örebro',                  latMin: 58.8,  latMax: 59.8,  lonMin: 14.3,  lonMax: 15.8  },
  { country: 'Sverige', name: 'Dalarna',                 latMin: 60.0,  latMax: 62.3,  lonMin: 12.8,  lonMax: 16.5  },
  // Norrland sør
  { country: 'Sverige', name: 'Gävleborg',               latMin: 60.4,  latMax: 62.2,  lonMin: 14.8,  lonMax: 17.5  },
  { country: 'Sverige', name: 'Västernorrland',          latMin: 62.0,  latMax: 63.6,  lonMin: 15.0,  lonMax: 19.2  },
  { country: 'Sverige', name: 'Jämtland',                latMin: 62.0,  latMax: 65.2,  lonMin: 12.0,  lonMax: 16.8  },
  // Norrland nord
  { country: 'Sverige', name: 'Västerbotten',            latMin: 63.5,  latMax: 66.4,  lonMin: 14.5,  lonMax: 21.5  },
  { country: 'Sverige', name: 'Norrbotten',              latMin: 65.5,  latMax: 69.1,  lonMin: 17.0,  lonMax: 24.2  },
];

export const FINLAND_REGIONS: RegionBounds[] = [
  { country: 'Finland', name: 'Uusimaa',                latMin: 59.8, latMax: 60.8, lonMin: 23.0, lonMax: 26.6 },
  { country: 'Finland', name: 'Varsinais-Suomi',        latMin: 59.8, latMax: 61.0, lonMin: 21.0, lonMax: 23.8 },
  { country: 'Finland', name: 'Satakunta',              latMin: 60.8, latMax: 62.0, lonMin: 20.8, lonMax: 23.3 },
  { country: 'Finland', name: 'Kanta-Häme',             latMin: 60.5, latMax: 61.5, lonMin: 23.2, lonMax: 25.5 },
  { country: 'Finland', name: 'Pirkanmaa',              latMin: 61.0, latMax: 62.4, lonMin: 22.7, lonMax: 25.5 },
  { country: 'Finland', name: 'Päijät-Häme',            latMin: 60.7, latMax: 61.7, lonMin: 24.8, lonMax: 26.7 },
  { country: 'Finland', name: 'Kymenlaakso',            latMin: 60.2, latMax: 61.3, lonMin: 26.0, lonMax: 28.2 },
  { country: 'Finland', name: 'Etelä-Karjala',          latMin: 60.7, latMax: 61.8, lonMin: 27.0, lonMax: 30.0 },
  { country: 'Finland', name: 'Etelä-Savo',             latMin: 61.2, latMax: 63.0, lonMin: 25.6, lonMax: 29.8 },
  { country: 'Finland', name: 'Pohjois-Savo',           latMin: 62.2, latMax: 64.0, lonMin: 26.0, lonMax: 29.6 },
  { country: 'Finland', name: 'Pohjois-Karjala',        latMin: 61.8, latMax: 64.4, lonMin: 28.0, lonMax: 31.6 },
  { country: 'Finland', name: 'Keski-Suomi',            latMin: 61.7, latMax: 63.8, lonMin: 24.0, lonMax: 27.8 },
  { country: 'Finland', name: 'Etelä-Pohjanmaa',        latMin: 61.8, latMax: 63.6, lonMin: 21.0, lonMax: 24.4 },
  { country: 'Finland', name: 'Pohjanmaa',              latMin: 62.3, latMax: 64.2, lonMin: 20.8, lonMax: 23.8 },
  { country: 'Finland', name: 'Keski-Pohjanmaa',        latMin: 63.0, latMax: 64.3, lonMin: 23.0, lonMax: 25.5 },
  { country: 'Finland', name: 'Pohjois-Pohjanmaa',      latMin: 63.6, latMax: 66.8, lonMin: 23.0, lonMax: 30.2 },
  { country: 'Finland', name: 'Kainuu',                 latMin: 63.8, latMax: 65.8, lonMin: 26.8, lonMax: 31.0 },
  { country: 'Finland', name: 'Lappi',                  latMin: 65.5, latMax: 70.1, lonMin: 20.5, lonMax: 30.2 },
  { country: 'Finland', name: 'Ahvenanmaa',             latMin: 59.8, latMax: 60.7, lonMin: 19.0, lonMax: 21.2 },
];

export const DENMARK_REGIONS: RegionBounds[] = [
  { country: 'Danmark', name: 'Nordjylland',            latMin: 56.6, latMax: 57.8, lonMin: 8.0,  lonMax: 11.6 },
  { country: 'Danmark', name: 'Midtjylland',            latMin: 55.7, latMax: 56.9, lonMin: 8.0,  lonMax: 11.5 },
  { country: 'Danmark', name: 'Syddanmark',             latMin: 54.7, latMax: 56.0, lonMin: 8.0,  lonMax: 11.0 },
  { country: 'Danmark', name: 'Sjælland',               latMin: 54.7, latMax: 56.0, lonMin: 11.0, lonMax: 13.0 },
  { country: 'Danmark', name: 'Hovedstaden',            latMin: 55.5, latMax: 56.3, lonMin: 11.6, lonMax: 15.3 },
];

export const NORDIC_PRESCAN_GROUPS: Array<{ country: RegionBounds['country']; regions: RegionBounds[] }> = [
  { country: 'Norge', regions: NORWAY_REGIONS },
  { country: 'Sverige', regions: SWEDEN_REGIONS },
  { country: 'Finland', regions: FINLAND_REGIONS },
  { country: 'Danmark', regions: DENMARK_REGIONS },
];

export const ALL_PRESCAN_REGIONS = NORDIC_PRESCAN_GROUPS.flatMap(group => group.regions);

const REGION_COUNTRY_LOOKUP = new Map<string, RegionBounds['country']>(
  ALL_PRESCAN_REGIONS.map(region => [region.name, region.country])
);

export function getPrescanCountryForRegion(regionName: string): RegionBounds['country'] | 'Andre' {
  return REGION_COUNTRY_LOOKUP.get(regionName) || 'Andre';
}

export function generateGrid(bounds: RegionBounds, cellLat = CELL_LAT, cellLon = CELL_LON): GridCell[] {
  const cells: GridCell[] = [];
  for (let lat = bounds.latMin; lat < bounds.latMax - 0.001; lat = +(lat + cellLat).toFixed(6)) {
    for (let lon = bounds.lonMin; lon < bounds.lonMax - 0.001; lon = +(lon + cellLon).toFixed(6)) {
      cells.push({
        latMin: +lat.toFixed(6),
        latMax: +Math.min(lat + cellLat, bounds.latMax).toFixed(6),
        lonMin: +lon.toFixed(6),
        lonMax: +Math.min(lon + cellLon, bounds.lonMax).toFixed(6),
      });
    }
  }
  return cells;
}
